import { useState, useCallback } from 'react';

interface UseRateLimiterOptions {
  /** Minimum time between submissions in milliseconds */
  minInterval?: number;
  /** Maximum submissions allowed within the time window */
  maxAttempts?: number;
  /** Time window for max attempts in milliseconds */
  windowMs?: number;
}

interface RateLimiterState {
  lastSubmitTime: number;
  attempts: number[];
}

/**
 * Client-side rate limiter hook to prevent rapid form submissions
 * and abuse of public endpoints.
 */
export function useRateLimiter({
  minInterval = 5000, // 5 seconds between submissions
  maxAttempts = 5,    // Max 5 attempts
  windowMs = 300000,  // Within 5 minutes
}: UseRateLimiterOptions = {}) {
  const [state, setState] = useState<RateLimiterState>({
    lastSubmitTime: 0,
    attempts: [],
  });

  const checkRateLimit = useCallback((): { allowed: boolean; waitTime?: number; message?: string } => {
    const now = Date.now();
    
    // Check minimum interval between submissions
    const timeSinceLastSubmit = now - state.lastSubmitTime;
    if (state.lastSubmitTime > 0 && timeSinceLastSubmit < minInterval) {
      const waitTime = Math.ceil((minInterval - timeSinceLastSubmit) / 1000);
      return {
        allowed: false,
        waitTime,
        message: `Please wait ${waitTime} seconds before submitting again.`,
      };
    }

    // Check max attempts within window
    const recentAttempts = state.attempts.filter(time => now - time < windowMs);
    if (recentAttempts.length >= maxAttempts) {
      const oldestAttempt = Math.min(...recentAttempts);
      const waitTime = Math.ceil((windowMs - (now - oldestAttempt)) / 1000 / 60);
      return {
        allowed: false,
        waitTime: waitTime * 60,
        message: `Too many submissions. Please try again in ${waitTime} minute${waitTime !== 1 ? 's' : ''}.`,
      };
    }

    return { allowed: true };
  }, [state, minInterval, maxAttempts, windowMs]);

  const recordSubmission = useCallback(() => {
    const now = Date.now();
    setState(prev => ({
      lastSubmitTime: now,
      attempts: [...prev.attempts.filter(time => now - time < windowMs), now],
    }));
  }, [windowMs]);

  const reset = useCallback(() => {
    setState({ lastSubmitTime: 0, attempts: [] });
  }, []);

  return {
    checkRateLimit,
    recordSubmission,
    reset,
  };
}
